import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { isRtl } from "@orderhub/shared";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
} from "react-router";
import { Toaster } from "sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { capturePostHogPageView } from "./lib/hooks/usePostHog";
import "./lib/i18n";
import { PHProvider } from "./lib/providers/PostHogProvider";
import { initSentry } from "./lib/sentry";
import "./styles/globals.css";

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){try{var l=localStorage.getItem('orderhub.preLoginLocale');if(!l){l=(navigator.language||'en').split('-')[0];}document.documentElement.lang=l;document.documentElement.dir=l==='ar'?'rtl':'ltr';}catch(e){}})();",
          }}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning>
        <TooltipProvider>{children}</TooltipProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const rtl = isRtl(i18n.language);

  useEffect(() => {
    if (location.pathname.startsWith("/app")) {
      capturePostHogPageView(location.pathname, location.search);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const applyDir = (lng: string) => {
      document.documentElement.lang = lng;
      document.documentElement.dir = isRtl(lng) ? "rtl" : "ltr";
    };
    applyDir(i18n.language);
    i18n.on("languageChanged", applyDir);
    return () => {
      i18n.off("languageChanged", applyDir);
    };
  }, [i18n]);

  return (
    <PHProvider>
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster
          position={rtl ? "top-left" : "top-right"}
          closeButton
          dir={rtl ? "rtl" : "ltr"}
        />
      </QueryClientProvider>
    </PHProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  Sentry.captureException(error);
  return (
    <div
      style={{ padding: "2rem", fontFamily: "sans-serif", textAlign: "center" }}
    >
      <h1>Something went wrong</h1>
      <p>An unexpected error occurred. Our team has been notified.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: "0.5rem 1rem",
          fontSize: "1rem",
          cursor: "pointer",
        }}
      >
        Reload page
      </button>
    </div>
  );
}
